import type { MakeName } from "@/types/make";
import type { UseFormReturnType } from "@mantine/form";
import type { TFunction } from "i18next";
import type { OnBoardingFormValues } from "../types";

export function getCountries(t: TFunction<"translation", undefined>) {
  return [
    { label: t("components.onboarding.countries.France"), value: "France" },
    { label: t("components.onboarding.countries.Germany"), value: "Germany" },
    { label: t("components.onboarding.countries.Italy"), value: "Italy" },
    { label: t("components.onboarding.countries.Spain"), value: "Spain" },
    { label: t("components.onboarding.countries.Switzerland"), value: "Switzerland" },
    { label: t("components.onboarding.countries.UAE"), value: "United Arab Emirates" },
    { label: t("components.onboarding.countries.UK"), value: "United Kingdom" },
    { label: t("components.onboarding.countries.USA"), value: "United States" },
    { label: t("components.onboarding.countries.Other"), value: "Other" },
  ];
}

export function getCountryOptionValue(
  value: string | undefined,
  t: TFunction<"translation", undefined>
) {
  if (!value) return "";

  return getCountries(t).some((country) => country.value === value) ? value : "Other";
}

export function toggleEraHandler(form: UseFormReturnType<OnBoardingFormValues>) {
  return (era: string) => {
    let current = form.values.selectedEras || [];

    if (current.includes(era)) {
      current = current.filter((e) => e !== era);
    } else {
      if (current.length < 5) {
        current = [...current, era];
      }
    }

    form.setFieldValue("selectedEras", current);
  };
}

export function formStepsHandler(
  activeStep: number,
  newName: string,
  form: UseFormReturnType<OnBoardingFormValues>
) {
  return () => {
    if (activeStep === 0) {
      if (!newName.trim()) return false;
      if (!form.values.countryOfResidence) return false;
      if (form.values.countryOfResidence === "Other" && !form.values.otherCountry.trim())
        return false;
      return true;
    }

    if (activeStep === 1) {
      const checkboxesSelected =
        form.values.trackInvestments ||
        form.values.followMakes ||
        form.values.followAuctions ||
        form.values.researchTrends ||
        form.values.comparePrices ||
        (form.values.hasOtherPreferences && form.values.otherPreferencesText.trim() !== "");

      const collectorProfileValid =
        form.values.collectorProfile.trim() !== "" &&
        (form.values.collectorProfile !== "Something else" ||
          form.values.collectorProfileOtherText.trim() !== "");

      return checkboxesSelected && collectorProfileValid;
    }

    if (activeStep === 2) {
      const selectedMakes = form.values.selectedMakes || [];

      // valid if at least one concrete make (not "Others") is selected
      const selectedMakesValid = selectedMakes.filter((m) => m !== "Others").length > 0;

      // valid if "Others" is selected AND the otherFavoriteMakes text is non-empty
      const otherMakesValid =
        selectedMakes.includes("Others") &&
        typeof form.values.otherFavoriteMakes === "string" &&
        form.values.otherFavoriteMakes.trim().length > 0;

      return selectedMakesValid || otherMakesValid;
    }

    if (activeStep === 3) {
      const erasValid = (form.values.selectedEras?.length ?? 0) > 0;
      const segmentsValid = (form.values.selectedSegments?.length ?? 0) > 0;

      return erasValid && segmentsValid;
    }

    return false;
  };
}

export function toggleSegmentHandler(form: UseFormReturnType<OnBoardingFormValues>) {
  return (segment: string) => {
    let current = form.values.selectedSegments || [];

    if (current.includes(segment)) {
      current = current.filter((s) => s !== segment);
    } else {
      if (current.length < 5) {
        current = [...current, segment];
      }
    }

    form.setFieldValue("selectedSegments", current);
  };
}

export function toggleMakeHandler(form: UseFormReturnType<OnBoardingFormValues>) {
  return (make: MakeName | "Others") => {
    const selected = form.values.selectedMakes;

    if (selected.includes(make)) {
      const newSelected = selected.filter((m) => m !== make);
      form.setFieldValue("selectedMakes", newSelected);
      if (make === "Others") {
        form.setFieldValue("otherFavoriteMakes", undefined);
      }
    } else {
      form.setFieldValue("selectedMakes", [...selected, make]);
    }
  };
}

export function getSegmentOptions(t: TFunction<"translation", undefined>) {
  return [
    {
      label: t("components.onboarding.segments.rallyhomologationcars"),
      tooltip: t("components.onboarding.tooltips.1"),
    },
    {
      label: t("components.onboarding.segments.americanmuscle"),
      tooltip: t("components.onboarding.tooltips.2"),
    },
    {
      label: t("components.onboarding.segments.japaneseiconsjdm"),
      tooltip: t("components.onboarding.tooltips.3"),
    },
    {
      label: t("components.onboarding.segments.modernsportscars"),
      tooltip: t("components.onboarding.tooltips.4"),
    },
    {
      label: t("components.onboarding.segments.luxurygrandtourersgt"),
      tooltip: t("components.onboarding.tooltips.5"),
    },
    {
      label: t("components.onboarding.segments.offroadutilityicons"),
      tooltip: t("components.onboarding.tooltips.6"),
    },
    {
      label: t("components.onboarding.segments.vintageeuropeanclassics"),
      tooltip: t("components.onboarding.tooltips.7"),
    },
  ];
}

export function getCollectorOptions(t: TFunction<"translation", undefined>) {
  return [
    t("components.onboarding.collector.longtimecollector"),
    t("components.onboarding.collector.industryprofessional"),
    t("components.onboarding.collector.activecollector"),
    t("components.onboarding.collector.aspiringcollector"),
    t("components.onboarding.collector.enthusiastresearcher"),
    t("components.onboarding.collector.somethingelse"),
  ];
}
